'use client';

import { FloatingFocusManager, FloatingList, useMergeRefs } from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { ComboboxRootContext } from './context/combobox-root.context';
import { useComboboxOptionsList } from './hooks/use-combobox-options-list';
import { useComboboxContentRenderProps } from './hooks/use-combobox-renders';
import {
  comboboxContentPositionerStyles,
  comboboxContentRecipe,
} from './styles/combobox-content.styles';
import type { ComboboxContentPropsInterface } from './types/combobox-content.types';

/**
 * @since 10.0.0
 */
export const ComboboxContent = forwardRef<HTMLDivElement, ComboboxContentPropsInterface>(
  (props: ComboboxContentPropsInterface, forwardedRef) => {
    /**
     * Get render prop functions
     */
    const {
      renderEmptyState,
      renderGroup,
      renderHeading,
      renderLoadingState,
      renderOption,
      renderOptionLabel,
      renderOptionMedia,
      renderOptionMeta,
      ...nonRenderProps
    } = useComboboxContentRenderProps(props);

    const { combobox, dividers } = useContext(ComboboxRootContext);

    const {
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = nonRenderProps;

    const contentPositionerStyles = css(comboboxContentPositionerStyles);
    const contentStyles = css(comboboxContentRecipe.raw({ dividers }), cssProp);

    const mergedRefs = useMergeRefs([combobox.refs.setFloating, forwardedRef]);

    const content = useComboboxOptionsList({
      renderLoadingState,
      renderEmptyState,
      renderGroup,
      renderHeading,
      renderOption,
      renderOptionLabel,
      renderOptionMedia,
      renderOptionMeta,
    });

    const getInsideElements = () => {
      const elements = [];

      const insideClear = document.querySelector('[data-c11n-component="Combobox.Clear"]');
      if (insideClear) {
        elements.push(insideClear);
      }

      const insideChip = document.querySelector('[data-c11n-component="InputChip.Root"]');
      if (insideChip) {
        elements.push(insideChip);
      }

      return elements;
    };

    // Must return early, otherwise FloatingFocusManager won't update getInsideElements when mounted
    if (!combobox.isMounted) {
      return null;
    }

    return (
      <FloatingFocusManager
        context={combobox.context}
        // Must be -1 to work with virtual list navigation
        initialFocus={-1}
        // Do not close when focus is lost
        closeOnFocusOut={false}
        // Do not return focus to reference when lost
        returnFocus={false}
        // Allows you to specify elements that are considered as "inside" the floating element.
        // These will avoid being marked with `aria-hidden` and `data-floating-ui-inert` attributes.
        getInsideElements={getInsideElements}
        // User specific overrides
        {...userFocusManagerProps}
      >
        <FloatingList elementsRef={combobox.elementsRef} labelsRef={combobox.labelsRef}>
          <div
            ref={mergedRefs}
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="Combobox.ContentPositioner"
            aria-multiselectable={combobox.isMultiSelect || undefined}
            className={cx('c11n', contentPositionerStyles)}
            style={{
              ...combobox.floatingStyles,
              visibility: combobox.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            }}
            {...combobox.getFloatingProps(rest)}
          >
            <div
              data-c11n-version={__C11N_VERSION__}
              data-c11n-component="Combobox.Content"
              className={cx('c11n', contentStyles, className)}
              style={{ ...combobox.floatingTransitionStyles, ...style }}
            >
              {content}
            </div>
          </div>
        </FloatingList>
      </FloatingFocusManager>
    );
  },
);

ComboboxContent.displayName = 'Combobox.Content';
